import React, { useEffect, useState } from "react";
import { supabase } from "../supabase";
import { APP_SESSION_STATE_CHANGED_EVENT } from "../helpers/appSessionStateEvents";
import { safeUserErrorMessage } from "../helpers/safeErrors";
import { devLog } from "../helpers/devLog";
import { usePermissions } from "../hooks/usePermissions";
import { scopeOperationalActiveSessionsForActor } from "../helpers/scopedQueries";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { OpenSessionDesktop } from "./ops/OpenSessionDesktop";

function formatDuration(startTime) {
  if (!startTime) return "—";

  const start = new Date(startTime).getTime();
  const now = Date.now();
  const diffSeconds = Math.max(0, Math.floor((now - start) / 1000));

  const hrs = Math.floor(diffSeconds / 3600);
  const mins = Math.floor((diffSeconds % 3600) / 60);

  return `${hrs}h ${mins}m`;
}

function formatTime(ts) {
  if (!ts) return "—";

  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OpenSessionScreen({ onBack, onCancelSession, onStopSession }) {
  const { actor } = usePermissions();
  const isDesktop = useIsDesktopViewport();
  const [sessionData, setSessionData] = useState(null);
  const [duration, setDuration] = useState(
    formatDuration(sessionData?.session_start),
  );
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!actor) {
      return;
    }

    loadActiveSession();
  }, [actor]);

  useEffect(() => {
    function handleSessionStateChanged() {
      loadActiveSession();
    }

    window.addEventListener(
      APP_SESSION_STATE_CHANGED_EVENT,
      handleSessionStateChanged,
    );

    return () => {
      window.removeEventListener(
        APP_SESSION_STATE_CHANGED_EVENT,
        handleSessionStateChanged,
      );
    };
  }, []);

  async function loadActiveSession() {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user?.id || !actor) return;

    let query = supabase
      .from("active_sessions")
      .select("*")
      .eq("supabase_user_id", session.user.id)
      .eq("status", "active")
      .single();

    query = await scopeOperationalActiveSessionsForActor(query, supabase, actor);

    const { data, error } = await query;

    if (error) {
      console.error("OPEN SESSION LOAD ERROR:", error);
      return;
    }

    devLog("OPEN SESSION DATA loaded", { hasSession: Boolean(data) });

    setSessionData(data);
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setDuration(formatDuration(sessionData?.session_start));
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionData]);

  async function handleCancelSession() {
    if (!sessionData?.session_id) {
      alert("No active session found.");
      return;
    }

    const confirmed = window.confirm(
      "Cancel this active session? No time log will be created.",
    );

    if (!confirmed) return;

    setBusy(true);

    const { error } = await supabase
      .from("active_sessions")
      .delete()
      .eq("session_id", sessionData.session_id);
    localStorage.removeItem("crewpilot_active_session");

    setBusy(false);

    if (error) {
      console.error("CANCEL SESSION ERROR:", error);
      alert(safeUserErrorMessage(error, "Unable to cancel active session."));
      return;
    }

    alert("Active session cancelled.");

    if (onCancelSession) {
      onCancelSession();
    } else {
      onBack();
    }
  }

  if (isDesktop) {
    return (
      <OpenSessionDesktop
        sessionData={sessionData}
        duration={duration}
        busy={busy}
        onBack={onBack}
        onStopSession={onStopSession}
        onCancelSession={handleCancelSession}
      />
    );
  }

  if (!sessionData) {
    return (
      <div className="mission-control-screen">
        <div className="mission-header">
          <button className="mission-back-button" onClick={onBack}>
            ← Back
          </button>

          <h1 className="mission-title">Open Session</h1>

          <p className="mission-subtitle">No active session found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mission-control-screen">
      <div className="mission-header">
        <button className="mission-back-button" onClick={onBack}>
          ← Back
        </button>

        <h1 className="mission-title">Open Session</h1>

        <p className="mission-subtitle">
          Review or cancel the active workforce session.
        </p>
      </div>

      <div className="mission-section">
        <div className="mission-section-header progress">🟡 Active Session</div>

        <div className="mission-card-grid">
          <div className="mission-card progress">
            <h3>{sessionData.job_number || "Active Session"}</h3>

            <ul className="mission-bullet-list">
              <li>Employee: {sessionData.employee_name || "Unknown"}</li>
              <li>Status: {sessionData.status || "active"}</li>
              <li>
                Started:{" "}
                {new Date(sessionData.session_start).toLocaleString([], {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </li>
              <li>Running Time: {duration}</li>
              <li>Break Active: {sessionData.break_active ? "Yes" : "No"}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mission-section">
        <div className="mission-card-grid">
          <button
            className="mission-card complete"
            onClick={onStopSession}
            disabled={busy}
          >
            <h3>Stop Time</h3>
          </button>

          <button
            className="mission-card future"
            onClick={handleCancelSession}
            disabled={busy}
          >
            <h3>{busy ? "Cancelling..." : "Cancel Session"}</h3>
          </button>
        </div>
      </div>
    </div>
  );
}
